/* eslint-disable no-nested-ternary */
/* eslint-disable consistent-return */
/* eslint-disable react/destructuring-assignment */
import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Check,
  Cross,
  InfoCircle,
  AlertCircle,
  CircleCheck,
  RotateClockwiseDots,
  AlertTriangle,
} from '@saleshandy/icons';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { SkeletonLoading } from '@saleshandy/design-system';
import { Spinner } from 'react-bootstrap';
import Table from '../../../../../../../../../../shared/design-system/components/organisms/table';
import { PaginationShowHide } from '../../../../../../../../../../shared/design-system/components/organisms/table/types';
import {
  OverlayTooltip,
  OverlayPopover,
  Placement,
} from '../../../../../../../../../../shared/design-system/components/overlay';
import { EmailAccountAuthentication } from '../../../../../../../../types';
import { accessibleOnClick } from '../../../../../../../../../../shared/utils/accessible-on-click';
import FactorDetailsModal from '../../../../../../../../../../shared/components/email-account-setup-score/components/factor-details-modal';
import { FactorModalConfig } from '../../../../../../../../../../shared/components/email-account-setup-score/types';
import {
  getCopyContent,
  getDomainBlacklistContent,
  getDomainBlacklistFooter,
  getIPBlacklistContent,
  getIPBlacklistFooter,
  getLearnMoreContent,
  getPtrSummaryString,
} from '../../../../../../../../../../shared/components/email-account-setup-score/get-descriptions';
import { supportUrls } from '../../../../../../../../../../shared/utils/urls';
import {
  getBlacklistedCount,
  getValidBlackistURLs,
  VALID_BLACKLIST_URLS,
} from '../../../../../../../../../../shared/components/email-account-setup-score/helper';
import { isPtrStatusNotValid } from "../../../../../../../../../email-accounts/enums/email-account";
import YoutubeVideoModal from '../../../../../../../../../../shared/design-system/components/molecules/youtube-video-modal';
import { getEspIcon } from '../../../utils/esp-icon';
import {
  SeedTestPauseReason,
  SeedTestSenderEmailAccountPauseReasonMessages,
  EmailReputationStatus,
} from '../../../../../../../../enums/inbox-radar';
import { getTooltipText } from '../../../../../../../../../sequence/components/sequence-single-content/components/sequence-single-header-actions/utils/helper';
import { RequestStatus } from '../../../../../../../../../../shared/enums/request-status';
import { executeOnRequestStatus } from '../../../../../../../../../../shared/utils/execute-on-request-status';

// Add InfoPopover component similar to email accounts columns
const InfoPopover = ({
  popoverKey,
  isOpen,
  onToggle,
  tooltipRef,
}: {
  popoverKey: string;
  isOpen: boolean;
  onToggle: (show: boolean) => void;
  tooltipRef: (el: HTMLElement | null) => void;
}) => {
  const { t } = useTranslation();

  // Define popover content for each type
  const getPopoverContent = (key: string) => {
    switch (key) {
      case 'spf':
        return {
          description:
            t('messages.spf_records_verified_description') ||
            'Sender Policy Framework provides email authentication',
          learnMoreUrl: supportUrls.spfBlog,
        };
      case 'dkim':
        return {
          description:
            t('messages.dkim_records_verified_description') ||
            'DomainKeys Identified Mail provides email authentication',
          learnMoreUrl: supportUrls.dkimBlog,
        };
      case 'dmarc':
        return {
          description:
            t('messages.dmarc_records_verified_description') ||
            'Domain-based Message Authentication, Reporting and Conformance',
          learnMoreUrl: supportUrls.dmarcBlog,
        };
      case 'ptr':
        return {
          description:
            'Helps mail servers verify that the IP and domain are aligned, reducing spam suspicion and improving overall deliverability.',
          learnMoreUrl: supportUrls.ptrBlog,
        };
      case 'domainBlacklist':
        return {
          description: 'Checks if your domain is listed on any blacklists',
          learnMoreUrl: supportUrls.domainAndIPBlacklistBlog,
        };
      case 'ipBlacklist':
        return {
          description: 'Checks if your IP is listed on any blacklists',
          learnMoreUrl: supportUrls.domainAndIPBlacklistBlog,
        };
      default:
        return {
          description: '',
          learnMoreUrl: '',
        };
    }
  };

  const content = getPopoverContent(popoverKey);

  return (
    <div ref={tooltipRef}>
      <OverlayPopover
        className="permission-popover"
        show={isOpen}
        onToggle={onToggle}
        placement={Placement.Bottom}
        content={
          <>
            <div className="content">
              {content.description}{' '}
              <a
                href={content.learnMoreUrl}
                target="_blank"
                rel="noreferrer"
                className="link"
                style={{ whiteSpace: 'nowrap' }}
              >
                Learn More
              </a>
            </div>
            <div
              className="d-flex justify-content-center"
              {...accessibleOnClick(() => onToggle(false))}
            >
              <Cross className="cross-icon" />
            </div>
          </>
        }
      >
        <InfoCircle
          className={`authentication-info-icon cursor-pointer ${
            isOpen ? 'isInfoIconVisible' : 'isInfoIconNotVisible'
          }`}
        />
      </OverlayPopover>
    </div>
  );
};

const EmailAuthenticationTable: React.FC<{
  scrollContainerRef?: React.RefObject<HTMLDivElement>;
  emailReputationData?: any;
  hasMoreEmailReputation?: boolean;
  isLoading?: boolean;
  error?: any;
  onLoadMore?: () => void;
  isLoadingMore?: boolean;
  sendGetEmailAuthenticationRequest?: (
    seedTestId: string,
    payload: {
      senderEmailAccountIds?: number[];
      limit: number;
      page: number;
      reputationStatus?: EmailReputationStatus;
    },
  ) => void;
  testId?: string;
  emailAuthenticationReportResponse?: any;
  firebaseData?: any;
  emailReputationRequestStatus?: RequestStatus;
}> = ({
  scrollContainerRef,
  emailReputationData,
  isLoading,
  hasMoreEmailReputation,
  onLoadMore,
  isLoadingMore,
  sendGetEmailAuthenticationRequest,
  testId,
  emailAuthenticationReportResponse,
  firebaseData,
  emailReputationRequestStatus,
}) => {
  const { t } = useTranslation();
  const scrollHandlerRef = useRef<() => void>();
  const [showFactorModal, setShowFactorModal] = useState(false);
  const [factorModalConfig, setFactorModalConfig] = useState<FactorModalConfig>(
    null,
  );
  const [showYoutubeVideoModal, setShowYoutubeVideoModal] = useState(false);
  const [youtubeVideoURL, setYoutubeVideoURL] = useState('');

  // Add state for managing popovers
  const [activePopover, setActivePopover] = useState<string | null>(null);
  const tooltipRefs = useRef<Record<string, HTMLElement | null>>({});

  // Helper function to get Firebase data for a specific record
  const getFirebaseDataForRecord = (recordId: number | string) => {
    const firebaseIds = firebaseData ? Object.keys(firebaseData) : [];
    return firebaseIds.includes(recordId?.toString())
      ? firebaseData[recordId]
      : null;
  };

  // Helper function to get Firebase reputation data for a specific type
  const getFirebaseReputationData = (
    recordId: number | string,
    type: string,
  ) => {
    const firebaseItemData = getFirebaseDataForRecord(recordId);
    return firebaseItemData?.emailReputation?.[type];
  };

  // Helper function to get isValid with Firebase fallback
  const getIsValidWithFallback = (record: any, type: string) => {
    const firebaseReputationData = getFirebaseReputationData(
      record?.seedTestSenderEmailAccountId,
      type,
    );
    if (firebaseReputationData?.isValid !== undefined) {
      return firebaseReputationData.isValid;
    }
    return record?.authReputation?.[type]?.isValid;
  };

  // Helper function to get count with Firebase fallback
  const getCountWithFallback = (record: any, type: string) => {
    const firebaseReputationData = getFirebaseReputationData(
      record?.seedTestSenderEmailAccountId,
      type,
    );
    if (firebaseReputationData?.count !== undefined) {
      return firebaseReputationData.count;
    }
    return record?.authReputation?.[type]?.listedCount;
  };

  const handleScroll = useCallback(() => {
    if (scrollContainerRef?.current) {
      const {
        scrollTop,
        scrollHeight,
        clientHeight,
      } = scrollContainerRef.current;
      // Load more when the user is 200px from the bottom
      if (scrollHeight - scrollTop <= clientHeight + 200) {
        if (hasMoreEmailReputation && !isLoadingMore && onLoadMore) {
          onLoadMore();
        }
      }
    }
  }, [hasMoreEmailReputation, isLoadingMore, onLoadMore]);

  scrollHandlerRef.current = handleScroll;

  useEffect(() => {
    const container = scrollContainerRef?.current;
    if (container) {
      const scrollHandler = () => scrollHandlerRef.current?.();
      container.addEventListener('scroll', scrollHandler);
      return () => container.removeEventListener('scroll', scrollHandler);
    }
  }, [scrollContainerRef]);

  useEffect(() => {
    if (!activePopover) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (!tooltipRefs.current[activePopover]?.contains(event.target as Node)) {
        setActivePopover(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [activePopover]);

  useEffect(
    () => () => {
      tooltipRefs.current = {};
    },
    [],
  );

  // Effect to handle opening modal when API request succeeds
  useEffect(() => {
    executeOnRequestStatus({
      status: emailReputationRequestStatus,
      onSuccess: () => {
        // Open the modal when API succeeds
        if (factorModalConfig) {
          setShowFactorModal(true);
        }
      },
    });
  }, [emailReputationRequestStatus, factorModalConfig]);

  const showYoutubeVideoHandler = (url: string) => {
    setYoutubeVideoURL(url);
    setShowYoutubeVideoModal(true);
  };

  const hideYoutubeVideoHandler = () => {
    setShowYoutubeVideoModal(false);
  };

  const getFirebaseDataForId = (seedTestSenderEmailAccountId: number) => {
    // Get all available IDs from Firebase data
    const firebaseIds = firebaseData ? Object.keys(firebaseData) : [];
    // Find matching Firebase data for current item's ID
    const firebaseItemData = firebaseIds.includes(
      seedTestSenderEmailAccountId?.toString(),
    )
      ? firebaseData[seedTestSenderEmailAccountId]
      : null;
    return firebaseItemData;
  };

  const getPauseReasonIdentifier = (row: any) => {
    const fbData = getFirebaseDataForId(row?.seedTestSenderEmailAccountId);
    return (
      (fbData?.pausedReasonIdentifier !== null &&
        fbData?.pausedReasonIdentifier) ||
      row.pausedReasonIdentifier
    );
  };

  // Helper function to check if should show pause warning
  const shouldShowPauseWarning = (row: any) => {
    const pauseReason = getPauseReasonIdentifier(row);
    return (
      pauseReason && pauseReason !== SeedTestPauseReason.ManuallyPausedByUser
    );
  };

  const handleFactorModal = (
    record: EmailAccountAuthentication,
    modalName: string,
  ) => {
    let modalConfig: FactorModalConfig = {
      title: '',
    };

    // Get Firebase data for current record
    const firebaseItemData = getFirebaseDataForRecord(
      record?.seedTestSenderEmailAccountId,
    );
    const firebaseReputationData = firebaseItemData?.emailReputation;

    // Get API response data for current record
    const apiResponseData = emailAuthenticationReportResponse?.emailAccounts?.find(
      (account: any) =>
        account.seedTestSenderEmailAccountId ===
        record?.seedTestSenderEmailAccountId,
    )?.authReputation;

    // Check if we need to fetch data based on the modal type
    let shouldFetchData = false;

    switch (modalName) {
      case 'spf':
        shouldFetchData =
          record?.authReputation?.spf?.isValid !==
            firebaseReputationData?.spf?.isValid &&
          firebaseReputationData !== undefined;
        break;
      case 'dmarc':
        shouldFetchData =
          record?.authReputation?.dmarc?.isValid !==
            firebaseReputationData?.dmarc?.isValid &&
          firebaseReputationData !== undefined;
        break;
      case 'dkim':
        shouldFetchData =
          record?.authReputation?.dkim?.isValid !==
            firebaseReputationData?.dkim?.isValid &&
          firebaseReputationData !== undefined;
        break;
      case 'ptr':
        shouldFetchData =
          record?.authReputation?.ptr?.status !==
            firebaseReputationData?.ptr?.status &&
          firebaseReputationData !== undefined;
        break;
      case 'domainBlacklist':
        shouldFetchData =
          record?.authReputation?.domainBlacklist?.listedCount !==
            firebaseReputationData?.domainBlacklist?.count &&
          firebaseReputationData !== undefined;
        break;
      case 'ipBlacklist':
        shouldFetchData =
          record?.authReputation?.ipBlacklist?.listedCount !==
            firebaseReputationData?.ipBlacklist?.count &&
          firebaseReputationData !== undefined;
        break;
      default:
        shouldFetchData = false;
    }

    // Helper function to get data with Firebase fallback
    const getDataWithFallback = (type: string, field: string) => {
      // First try API response data
      if (apiResponseData?.[type]?.[field] !== undefined) {
        return apiResponseData[type][field];
      }
      // Then try Firebase data
      if (firebaseReputationData?.[type]?.[field] !== undefined) {
        return firebaseReputationData[type][field];
      }
      // Finally fall back to original data
      return record?.authReputation?.[type]?.[field];
    };

    // Helper function to get isValid with Firebase fallback
    const getIsValid = (type: string) => {
      if (firebaseReputationData?.[type]?.isValid !== undefined) {
        return firebaseReputationData[type].isValid;
      }
      return record?.authReputation?.[type]?.isValid;
    };

    // Helper function to get count with Firebase fallback
    const getCount = (type: string) => {
      if (firebaseReputationData?.[type]?.count !== undefined) {
        return firebaseReputationData[type].count;
      }
      return record?.authReputation?.[type]?.listedCount;
    };

    switch (modalName) {
      case 'spf': {
        const spfIsValid = getIsValid('spf');
        const spfRecord =
          getDataWithFallback('spf', 'spfRecord') ||
          getDataWithFallback('spf', 'spfResults');

        modalConfig = {
          title: (
            <span className="d-flex align-items-center gap-8">
              {spfIsValid ? (
                <CircleCheck height={24} width={24} color="#047857" />
              ) : (
                <AlertCircle height={24} width={24} color="#D97706" />
              )}
              <span>
                {spfIsValid
                  ? t('messages.spf_records_verified')
                  : t('messages.spf_record_not_found')}
              </span>
            </span>
          ),
          content: (
            <>
              <div className="modal-description">
                <span>
                  {spfIsValid
                    ? t('messages.spf_records_verified_description')
                    : t('messages.spf_record_not_found_description')}
                </span>
              </div>
              {spfIsValid
                ? getCopyContent(spfRecord)
                : getLearnMoreContent('SPF', supportUrls.spfBlog)}
            </>
          ),
          width: 'small',
        };
        break;
      }
      case 'dmarc': {
        const dmarcIsValid = getIsValid('dmarc');
        const dmarcRecord =
          getDataWithFallback('dmarc', 'dmarcRecord') ||
          getDataWithFallback('dmarc', 'dmarcResults');

        modalConfig = {
          title: (
            <span className="d-flex align-items-center gap-8">
              {dmarcIsValid ? (
                <CircleCheck height={24} width={24} color="#047857" />
              ) : (
                <AlertCircle height={24} width={24} color="#D97706" />
              )}
              <span>
                {dmarcIsValid
                  ? t('messages.dmarc_records_verified')
                  : t('messages.dmarc_record_not_detected')}
              </span>
            </span>
          ),
          content: dmarcIsValid ? (
            <>
              <div className="modal-description">
                <span>
                  {dmarcIsValid
                    ? t('messages.dmarc_records_verified_description')
                    : t('messages.dmarc_record_not_detected_description')}
                </span>
              </div>
              {dmarcIsValid
                ? getCopyContent(dmarcRecord)
                : getLearnMoreContent('DMARC', supportUrls.dmarcBlog)}
            </>
          ) : (
            <>
              <div className="modal-description">
                <span>
                  {t('messages.dmarc_record_not_detected_description')}
                </span>
              </div>
            </>
          ),
          width: 'small',
        };
        break;
      }
      case 'dkim': {
        const dkimIsValid = getIsValid('dkim');
        const dkimRecord = getDataWithFallback('dkim', 'dkimRecord');

        modalConfig = {
          title: (
            <span className="d-flex align-items-center gap-8">
              {dkimIsValid ? (
                <CircleCheck height={24} width={24} color="#047857" />
              ) : (
                <AlertCircle height={24} width={24} color="#D97706" />
              )}
              <span>
                {dkimIsValid
                  ? t('messages.dkim_records_verified')
                  : t('messages.dkim_record_not_found')}
              </span>
            </span>
          ),
          content: (
            <>
              <div className="modal-description">
                <span>
                  {dkimIsValid
                    ? t('messages.dkim_records_verified_description')
                    : t('messages.dkim_record_not_found_description')}
                </span>
              </div>
              {dkimIsValid
                ? getCopyContent(
                    Array.isArray(dkimRecord)
                      ? dkimRecord.join(',')
                      : dkimRecord,
                  )
                : getLearnMoreContent('DKIM', supportUrls.dkimBlog)}
            </>
          ),
          width: 'small',
        };
        break;
      }
      case 'ptr': {
        const ptrStatus = getDataWithFallback('ptr', 'status');
        const ptrSendingIp = getDataWithFallback('ptr', 'sending_ip');
        const ptrDomain = getDataWithFallback('domainBlacklist', 'domain') ?? '';
        const ptrValid =
          ptrStatus != null && !isPtrStatusNotValid(ptrStatus);

        modalConfig = {
          title: (
            <span className="d-flex align-items-center gap-8">
              {ptrValid ? (
                <CircleCheck height={24} width={24} color="#047857" />
              ) : (
                <AlertCircle height={24} width={24} color="#D97706" />
              )}
              <span>
                {ptrValid
                  ? t('messages.ptr_records_verified')
                  : t('messages.ptr_record_not_found')}
              </span>
            </span>
          ),
          content: (
            <>
              <div className="modal-description">
                <span>
                  {ptrValid
                    ? t('messages.ptr_records_verified_description')
                    : t('messages.ptr_record_not_found_description')}
                </span>
              </div>
              {ptrValid ? (
                ptrStatus === 'not_required' ? (
                  getCopyContent(t('messages.ptr_managed_by_provider'))
                ) : (
                  getCopyContent(
                    getPtrSummaryString(ptrSendingIp ?? '', ptrDomain),
                  )
                )
              ) : (
                getLearnMoreContent('PTR', supportUrls.ptrBlog)
              )}
            </>
          ),
          width: 'small',
        };
        break;
      }
      case 'domainBlacklist': {
        const domainBlacklistCount = getCount('domainBlacklist');
        const domainBlacklistData = getDataWithFallback(
          'domainBlacklist',
          'data',
        );

        if (domainBlacklistCount >= 0) {
          const validDomainBlacklistData = getValidBlackistURLs(
            domainBlacklistData || [],
          );
          const domainBlacklistRecord = getDataWithFallback(
            'domainBlacklist',
            'domain',
          );
          const domainBlacklistIp = getDataWithFallback(
            'domainBlacklist',
            'ip',
          );

          modalConfig = {
            title: t('messages.domain_blacklist_title'),
            content: getDomainBlacklistContent({
              domain: domainBlacklistRecord,
              ip: domainBlacklistIp,
              data: domainBlacklistData || [],
            }),
            columns: [
              {
                title: 'Status',
                dataIndex: 'status',
                key: 'status',
                align: 'left',
              },
              {
                title: t('messages.modal_col_blacklist'),
                dataIndex: 'blacklist',
                key: 'blacklist',
                align: 'left',
                render: (cell) => (
                  <span>
                    {VALID_BLACKLIST_URLS[cell] !== '' ? (
                      <a
                        href={VALID_BLACKLIST_URLS[cell]}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {cell}
                      </a>
                    ) : (
                      cell
                    )}
                  </span>
                ),
              },
            ],
            data: validDomainBlacklistData,
            footer:
              getBlacklistedCount(domainBlacklistData || []) > 0
                ? getDomainBlacklistFooter(() =>
                    showYoutubeVideoHandler(
                      supportUrls.domainIPBlacklistVideoUrl,
                    ),
                  )
                : null,
            onLearnMore: () => {
              window.open(supportUrls.domainAndIPBlacklistBlog, '_blank');
              setShowFactorModal(false);
            },
          };
          break;
        }
        break;
      }
      case 'ipBlacklist': {
        const ipBlacklistCount = getCount('ipBlacklist');
        const ipBlacklistData = getDataWithFallback('ipBlacklist', 'data');

        if (ipBlacklistCount >= 0) {
          const validIpBlacklistData = getValidBlackistURLs(
            ipBlacklistData || [],
          );
          const ipBlacklistRecord = getDataWithFallback('ipBlacklist', 'ip');

          modalConfig = {
            title: t('messages.ip_blacklist_title'),
            content: getIPBlacklistContent({
              ip: ipBlacklistRecord,
              data: ipBlacklistData || [],
            }),
            columns: [
              {
                title: 'Status',
                dataIndex: 'status',
                key: 'status',
                align: 'left',
              },
              {
                title: t('messages.modal_col_blacklist'),
                dataIndex: 'blacklist',
                key: 'blacklist',
                align: 'left',
                render: (cell) => (
                  <span>
                    {VALID_BLACKLIST_URLS[cell] !== '' ? (
                      <a
                        href={VALID_BLACKLIST_URLS[cell]}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {cell}
                      </a>
                    ) : (
                      cell
                    )}
                  </span>
                ),
              },
            ],
            data: validIpBlacklistData,
            footer:
              getBlacklistedCount(ipBlacklistData || []) > 0
                ? getIPBlacklistFooter(() =>
                    showYoutubeVideoHandler(
                      supportUrls.domainIPBlacklistVideoUrl,
                    ),
                  )
                : null,
            onLearnMore: () => {
              window.open(supportUrls.domainAndIPBlacklistBlog, '_blank');
              setShowFactorModal(false);
            },
          };
          break;
        }
        break;
      }
      default:
        break;
    }
    if (modalConfig.title === '') {
      return;
    }

    // If we need to fetch data, set modal config and make API call
    if (shouldFetchData) {
      setFactorModalConfig(modalConfig);
      sendGetEmailAuthenticationRequest(testId, {
        senderEmailAccountIds: [record?.seedTestSenderEmailAccountId],
        limit: 1,
        page: 1,
        reputationStatus: EmailReputationStatus.Mixed,
      });
      return;
    }

    // If no API call needed, open modal immediately
    setFactorModalConfig(modalConfig);
    setShowFactorModal(true);
  };

  const renderBlacklistCell = (type: 'domainBlacklist' | 'ipBlacklist') => (
    cell: any,
    row: any,
  ) => {
    const count = getCountWithFallback(row, type);

    const cellClassName = classNames('cursor-pointer', {
      'font-weight-medium': count !== undefined && count > 0,
      'color-red': count > 2,
      'color-orange': count <= 2 && count > 0,
      'color-green': count === 0,
    });
    // Only show "For free domains we don't check blacklist" if Firebase data is not available
    if (row?.authReputation?.[type]?.isFreeDomainOrIp) {
      return (
        <OverlayTooltip
          className="blacklist-tooltip"
          placement={Placement.Bottom}
          text="For free domains we don't check blacklist"
        >
          <span className="blacklist-tooltip">-</span>
        </OverlayTooltip>
      );
    }
    if (count === -1) {
      return (
        <OverlayTooltip
          className="blacklist-tooltip"
          placement={Placement.Bottom}
          text="In-progress"
        >
          <RotateClockwiseDots />
        </OverlayTooltip>
      );
    }

    // const authReputation = emailAuthenticationReportResponse?.emailAccounts?.map(
    //   (auth) => auth.authReputation,
    // );

    return (
      <div
        className={cellClassName}
        role="button"
        tabIndex={0}
        onClick={() => handleFactorModal(row, type)}
        onKeyPress={(e) => {
          if (e.key === 'Enter') handleFactorModal(row, type);
        }}
      >
        {(() => {
          if (count !== undefined && count >= 0) {
            return (
              <OverlayTooltip text="View List" placement={Placement.Bottom}>
                <span>{count}</span>
              </OverlayTooltip>
            );
          }
          return <span>-</span>;
        })()}
      </div>
    );
  };

  return (
    <>
      <div className="email-authentication-table-container w-100">
        <Table
          columns={[
            {
              dataField: 'fromEmail',
              text: 'Sender Email Accounts',
              headerAlign: 'start',
              component: (cell, row) => {
                const pauseReason = getPauseReasonIdentifier(row);
                return (
                  <div className="d-flex align-items-center gap-8">
                    {getEspIcon(row?.emailType)}
                    <span className="email-address">{row?.fromEmail}</span>
                    {shouldShowPauseWarning(row) && (
                      <OverlayTooltip
                        text={getTooltipText(
                          SeedTestSenderEmailAccountPauseReasonMessages[
                            pauseReason
                          ],
                          t,
                        )}
                        placement={Placement.Auto}
                      >
                        <span className="sender-email-account-paused-reason">
                          <AlertTriangle color="#B91C1C" />
                        </span>
                      </OverlayTooltip>
                    )}
                  </div>
                );
              },
              headerLoadingSkeleton: (
                <SkeletonLoading width={100} height={20} />
              ),
              cellLoadingSkeleton: <SkeletonLoading width={100} height={20} />,
            },
            {
              dataField: 'authReputation.spf',
              text: 'SPF',
              headerComponent: () => (
                <div className="d-flex align-items-center gap-4">
                  <span>SPF</span>
                  <InfoPopover
                    popoverKey="spf"
                    isOpen={activePopover === 'spf'}
                    onToggle={(show) => setActivePopover(show ? 'spf' : null)}
                    tooltipRef={(el) => {
                      tooltipRefs.current.spf = el;
                    }}
                  />
                </div>
              ),
              headerAlign: 'start',
              component: (cell, row) => {
                const isValid = getIsValidWithFallback(row, 'spf');

                return (
                  <div
                    className="cursor-pointer"
                    {...accessibleOnClick(() => handleFactorModal(row, 'spf'))}
                  >
                    {isValid ? (
                      <OverlayTooltip
                        placement={Placement.Bottom}
                        text="View Record"
                      >
                        <Check className="check-cross-icon" color="#059669" />
                      </OverlayTooltip>
                    ) : (
                      <OverlayTooltip
                        className="blacklist-tooltip"
                        placement={Placement.Bottom}
                        text="View Setup guide"
                      >
                        <Cross className="check-cross-icon" color="#DC2626" />
                      </OverlayTooltip>
                    )}
                  </div>
                );
              },
              headerLoadingSkeleton: <SkeletonLoading width={62} height={14} />,
              cellLoadingSkeleton: (
                <SkeletonLoading width={32} height={24} borderRadius={20} />
              ),
            },
            {
              dataField: 'authReputation.dkim',
              text: 'DKIM',
              headerComponent: () => (
                <div className="d-flex align-items-center gap-4">
                  <span>DKIM</span>
                  <InfoPopover
                    popoverKey="dkim"
                    isOpen={activePopover === 'dkim'}
                    onToggle={(show) => setActivePopover(show ? 'dkim' : null)}
                    tooltipRef={(el) => {
                      tooltipRefs.current.dkim = el;
                    }}
                  />
                </div>
              ),
              headerAlign: 'start',
              component: (cell, row) => {
                const isValid = getIsValidWithFallback(row, 'dkim');

                return (
                  <div
                    className="cursor-pointer"
                    {...accessibleOnClick(() => handleFactorModal(row, 'dkim'))}
                  >
                    {isValid ? (
                      <OverlayTooltip
                        placement={Placement.Bottom}
                        text="View Record"
                      >
                        <Check className="check-cross-icon" color="#059669" />
                      </OverlayTooltip>
                    ) : (
                      <OverlayTooltip
                        className="blacklist-tooltip"
                        placement={Placement.Bottom}
                        text="View Setup guide"
                      >
                        <Cross className="check-cross-icon" color="#DC2626" />
                      </OverlayTooltip>
                    )}
                  </div>
                );
              },
              headerLoadingSkeleton: <SkeletonLoading width={62} height={14} />,
              cellLoadingSkeleton: (
                <SkeletonLoading width={32} height={24} borderRadius={20} />
              ),
            },
            {
              dataField: 'authReputation.dmarc',
              text: 'DMARC',
              headerComponent: () => (
                <div className="d-flex align-items-center gap-4">
                  <span>DMARC</span>
                  <InfoPopover
                    popoverKey="dmarc"
                    isOpen={activePopover === 'dmarc'}
                    onToggle={(show) => setActivePopover(show ? 'dmarc' : null)}
                    tooltipRef={(el) => {
                      tooltipRefs.current.dmarc = el;
                    }}
                  />
                </div>
              ),
              headerAlign: 'start',
              component: (cell, row) => {
                const isValid = getIsValidWithFallback(row, 'dmarc');

                return (
                  <div
                    className="cursor-pointer"
                    {...accessibleOnClick(() =>
                      handleFactorModal(row, 'dmarc'),
                    )}
                  >
                    {isValid ? (
                      <OverlayTooltip
                        placement={Placement.Bottom}
                        text="View Record"
                      >
                        <Check className="check-cross-icon" color="#059669" />
                      </OverlayTooltip>
                    ) : (
                      <OverlayTooltip
                        className="blacklist-tooltip"
                        placement={Placement.Bottom}
                        text="View Setup guide"
                      >
                        <Cross className="check-cross-icon" color="#DC2626" />
                      </OverlayTooltip>
                    )}
                  </div>
                );
              },
              headerLoadingSkeleton: <SkeletonLoading width={62} height={14} />,
              cellLoadingSkeleton: (
                <SkeletonLoading width={32} height={24} borderRadius={20} />
              ),
            },
            {
              dataField: 'authReputation.ptr',
              text: 'PTR (rDNS)',
              headerComponent: () => (
                <div className="d-flex align-items-center gap-4">
                  <span>PTR (rDNS)</span>
                  <InfoPopover
                    popoverKey="ptr"
                    isOpen={activePopover === 'ptr'}
                    onToggle={(show) => setActivePopover(show ? 'ptr' : null)}
                    tooltipRef={(el) => {
                      tooltipRefs.current.ptr = el;
                    }}
                  />
                </div>
              ),
              headerAlign: 'start',
              component: (cell, row) => {
                const ptrStatus =
                  getFirebaseReputationData(
                    row?.seedTestSenderEmailAccountId,
                    'ptr',
                  )?.status ?? row?.authReputation?.ptr?.status;
                const isValid =
                  ptrStatus != null && !isPtrStatusNotValid(ptrStatus);

                return (
                  <div
                    className="cursor-pointer"
                    {...accessibleOnClick(() => handleFactorModal(row, 'ptr'))}
                  >
                    {isValid ? (
                      <OverlayTooltip
                        placement={Placement.Bottom}
                        text="View Record"
                      >
                        <Check className="check-cross-icon" color="#059669" />
                      </OverlayTooltip>
                    ) : (
                      <OverlayTooltip
                        className="blacklist-tooltip"
                        placement={Placement.Bottom}
                        text="View Setup guide"
                      >
                        <Cross className="check-cross-icon" color="#DC2626" />
                      </OverlayTooltip>
                    )}
                  </div>
                );
              },
              headerLoadingSkeleton: <SkeletonLoading width={62} height={14} />,
              cellLoadingSkeleton: (
                <SkeletonLoading width={32} height={24} borderRadius={20} />
              ),
            },
            {
              dataField: 'authReputation.domainBlacklist',
              text: 'Domain Blacklist',
              headerComponent: () => (
                <div className="d-flex align-items-center gap-4">
                  <span>Domain Blacklist</span>
                  <InfoPopover
                    popoverKey="domainBlacklist"
                    isOpen={activePopover === 'domainBlacklist'}
                    onToggle={(show) =>
                      setActivePopover(show ? 'domainBlacklist' : null)
                    }
                    tooltipRef={(el) => {
                      tooltipRefs.current.domainBlacklist = el;
                    }}
                  />
                </div>
              ),
              headerAlign: 'start',
              component: (cell, row) =>
                renderBlacklistCell('domainBlacklist')(cell, row),
              headerLoadingSkeleton: <SkeletonLoading width={62} height={14} />,
              cellLoadingSkeleton: (
                <SkeletonLoading width={32} height={24} borderRadius={20} />
              ),
            },
            {
              dataField: 'authReputation.ipBlacklist',
              text: 'IP Blacklist',
              headerComponent: () => (
                <div className="d-flex align-items-center gap-4">
                  <span>IP Blacklist</span>
                  <InfoPopover
                    popoverKey="ipBlacklist"
                    isOpen={activePopover === 'ipBlacklist'}
                    onToggle={(show) =>
                      setActivePopover(show ? 'ipBlacklist' : null)
                    }
                    tooltipRef={(el) => {
                      tooltipRefs.current.ipBlacklist = el;
                    }}
                  />
                </div>
              ),
              headerAlign: 'start',
              component: (cell, row) =>
                renderBlacklistCell('ipBlacklist')(cell, row),
              headerLoadingSkeleton: <SkeletonLoading width={62} height={14} />,
              cellLoadingSkeleton: (
                <SkeletonLoading width={32} height={24} borderRadius={20} />
              ),
            },
          ]}
          data={emailReputationData || []}
          pagination={PaginationShowHide.HIDE}
          paginationOptions={{
            options: {
              limit: emailReputationData?.length || 0,
              page: 1,
              totalElements: emailReputationData?.length || 0,
            },
          }}
          loadingRowsCount={2}
          isLoading={isLoading && !isLoadingMore}
        />
        {isLoadingMore && (
          <div className="d-flex justify-content-center align-items-center m-4">
            <Spinner className="spinner--blue" animation="border" />
          </div>
        )}
      </div>
      <FactorDetailsModal
        show={showFactorModal}
        onClose={() => setShowFactorModal(false)}
        factorModalConfig={factorModalConfig}
      />
      <YoutubeVideoModal
        show={showYoutubeVideoModal}
        youtubeVideoUrl={youtubeVideoURL}
        onClose={hideYoutubeVideoHandler}
      />
    </>
  );
};

export default EmailAuthenticationTable;
